import { Icon } from '@buggy/shared'
import { Input, Spin, notification } from 'antd'
import { noop } from 'lodash-es'
import { FunctionComponent, useEffect, useState } from 'react'
import { useInput } from 'react-hanger'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { useAddReportKolsMutation } from '@/api/workspace-api/report-api'
import { BasicModal } from '@/components/common/antd/basic-modal'
import useResetCurrentFootprintPage from '@/components/kol/common/kol-cooperation-detail-drawer/footprint-history/use-reset-current-footprint-page'
import { VipOnlyModal } from '@/components/modal/vip-only-modal'
import { usePermissions } from '@/hooks/use-permissions'
import useReportLimit from '@/hooks/use-report-limit'
import { useSelector } from '@/hooks/use-selector'
import { useIntl } from '@/i18n/hooks/use-intl'
import { setOpen } from '@/store/add-kols-to-report-modal'
import type {
  FilterResource,
  ResourceKol,
} from '@/types/schema/directory/kol-management'
import { ampli } from '@/utils/ampli'
import { useReports } from '@/utils/hooks/use-reports'
import logger from '@/utils/logger'
import { isFetchBaseQueryError } from '@/utils/redux-toolkit'

enum StatusCode {
  ResourceLimitation = 403,
}

export const AddKolsToReportModal: FunctionComponent = () => {
  const dispatch = useDispatch()
  const resetCurrentFootprintPage = useResetCurrentFootprintPage()
  const { formatMessage, formatNumber } = useIntl()

  const [limitModalOpen, setLimitModalOpen] = useState(false)

  const { open, kols, from, isClickedFromDrawer } = useSelector(
    (state) => state.addKolsToReportModal,
  )

  const { limit } = useReportLimit()
  const { withFreePermission, withReportPermission } = usePermissions()
  const [addReportKols] = useAddReportKolsMutation()
  const {
    reports,
    loading: loadingReport,
    loadingRef: loadingReportRef,
    hasNextPage: hasNextReportPage,
    getReportsByName,
    refetch: refetchReports,
  } = useReports()
  const searchName = useInput('')

  const isSelectedSingleKol = open ? kols.length === 1 : false
  const selectedKolIds = open ? kols.map((kol) => kol.id) : []

  const handleCancel: VoidFunction = () => {
    dispatch(setOpen({ open: false }))
  }

  const handleAdd = async (selectedReport: FilterResource): Promise<void> => {
    if (!open) {
      return
    }

    try {
      await addReportKols({
        uuids: selectedKolIds,
        reportId: selectedReport.id,
      }).unwrap()

      const addedKols = kols.map(
        (kol): ResourceKol => ({
          uuid: kol.id,
          name: kol.name,
          follower_count: kol.follower,
          is_verified: kol.verified,
          is_radar_star: kol.isRadarStar,
        }),
      )

      refetchReports()

      ampli.addReportKol({
        kolIds: selectedKolIds,
        kolNames: kols.map((kol) => kol.name),
        kolCount: kols.length,
        reportId: selectedReport.id,
        reportName: selectedReport.name,
        reportKolAdded: addedKols,
        isMultiple: kols.length > 1,
        from,
        isClickedFromDrawer,
      })

      if (isClickedFromDrawer) {
        resetCurrentFootprintPage()
      }

      notification.success({
        message: formatMessage({
          id: 'campaign:notify_added_influencer',
        }),
        placement: 'bottomRight',
      })
    } catch (error) {
      logger.error(error)

      if (
        isFetchBaseQueryError(error) &&
        error.status === StatusCode.ResourceLimitation
      ) {
        setLimitModalOpen(true)
        return
      }

      notification.error({
        message: formatMessage({
          id: 'campaign:notify_add_influencer_fail',
        }),
        placement: 'bottomRight',
      })
    }
  }

  useEffect(() => {
    if (!withReportPermission) {
      return
    }

    if (!open) {
      searchName.setValue('')
      getReportsByName()
    }
  }, [open, withReportPermission, searchName, getReportsByName, refetchReports])

  if (!withReportPermission) {
    return null
  }

  return (
    <BasicModal
      destroyOnClose
      cancelButtonProps={{ style: { display: 'none' } }}
      getContainer={false}
      okButtonProps={{ style: { display: 'none' } }}
      open={open}
      style={{ maxWidth: '520px' }}
      title={formatMessage({ id: 'irm:community_add_to_report' })}
      width='100%'
      onCancel={handleCancel}
    >
      <div>
        <Input.Search
          {...searchName.eventBind}
          allowClear
          placeholder={formatMessage({ id: 'general:table_search_button' })}
          onSearch={(value): void => getReportsByName(value)}
        />
        <ResultWrapper>
          {reports.map((report) => {
            const inReport =
              isSelectedSingleKol &&
              report.kols?.some((kol) => kol.uuid === selectedKolIds[0])
            return (
              <ResourceWrapper key={report.id}>
                <TextWrapper>
                  <ResourceName>{report.name}</ResourceName>
                  <ResourceKOLAmount>
                    {formatNumber(report.kols?.length || 0)} KOL
                  </ResourceKOLAmount>
                </TextWrapper>
                <ActionWrapper
                  inReport={inReport}
                  onClick={(): void => {
                    inReport ? noop() : handleAdd(report)
                  }}
                >
                  <IconWrapper>
                    <Icon name={inReport ? 'check' : 'plus-square'} />
                  </IconWrapper>
                  {formatMessage({
                    id:
                      isSelectedSingleKol && inReport
                        ? 'project:add_kol_added_text'
                        : 'general:add_btn',
                  })}
                </ActionWrapper>
              </ResourceWrapper>
            )
          })}
          {(loadingReport || hasNextReportPage) && (
            <ResourceWrapper>
              <div ref={loadingReportRef}>
                <Spin />
              </div>
            </ResourceWrapper>
          )}
        </ResultWrapper>
      </div>
      <VipOnlyModal
        canContinueBrowsingFromProp={!withFreePermission}
        source='Deep Report KOL Limit'
        title={formatMessage(
          { id: 'error:deep_report_limit_reached_alert' },
          { count: limit.numberOfKols },
        )}
        visible={limitModalOpen}
        onClose={() => {
          setLimitModalOpen(false)
        }}
      />
    </BasicModal>
  )
}

const ResourceWrapper = styled.div`
  padding: 16px 8px;
  border-bottom: 1px solid
    ${({ theme }): string => theme.colors.base.grey.grey6};
  display: flex;
  justify-content: space-between;
`

const ResourceName = styled.div`
  font-size: 16px;
  line-height: 22px;
  color: ${(props): string => props.theme.colors.base.grey.grey1};
  max-width: 350px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const ResourceKOLAmount = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: #bfbfbf;
`

const TextWrapper = styled.div`
  margin: 0 20px 0 0;
`

interface ActionWrapperProps {
  inReport: boolean
}

const ActionWrapper = styled.div.attrs<ActionWrapperProps>((props) => ({
  inReport: props.inReport,
}))<ActionWrapperProps>`
  display: flex;
  align-items: center;
  cursor: pointer;
  .anticon {
    margin: 0 5px 0 0;
  }

  color: ${({ inReport, theme }): string =>
    inReport ? theme.colors.brand.primaryHover : 'unset'};

  &:hover {
    color: ${({ theme }): string => theme.colors.brand.primaryHover};
  }
`

const ResultWrapper = styled.div`
  max-height: 500px;
  overflow-y: scroll;
`

const IconWrapper = styled.div`
  margin: 0 5px 0 0;
  line-height: 1;
`
